// @ts-nocheck
import translate from "@insite/client-framework/Translate";
import { BaseFormFieldProps, FormFieldValue } from "@store/Framework/Types/FormField";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import React, { FC, ReactNode, useEffect, useMemo, useState } from "react";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@store/MobiusOverrides/Checkbox/Checkbox";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useProductState } from "@toolkit/Framework/Store/Data/Products/ProductsHooks";
import loadProduct from "@insite/client-framework/Store/Data/Products/Handlers/LoadProduct";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import { css } from "styled-components";
import getColor from "@insite/mobius/utilities/getColor";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import ReferenceProduct, { ReferenceProductPresentationProps } from "@store/Library/Components/ReferenceProduct";
import { LoadProductParameter } from "@store/Extensions/Store/Data/Products/Handlers/LoadProduct";
import getGuidFromQueryString from "@store/Framework/Common/Utilities/getGuidFromQueryString";
import constants from "@store/constants";

type FieldValueType = string;
export type ReferenceProductIdFormFieldValue = FormFieldValue<FieldValueType>;

export interface ReferenceProductIdFormFieldPresentationProps extends ExpandStyles<ReferenceProductIdFormFieldStyles> {}

export interface ReferenceProductIdFormFieldProps
    extends Omit<CheckboxProps, keyof CheckboxPresentationProps | "checked" | "error">,
        ReferenceProductIdFormFieldPresentationProps,
        BaseFormFieldProps<FieldValueType> {
    defaultChecked?: boolean;
    label?: ReactNode;
}

export interface ReferenceProductIdFormFieldStyles {
    checkbox?: CheckboxPresentationProps;
    checkboxWrapper?: InjectableCss;
    productWrapper?: InjectableCss;
    referenceProduct?: ReferenceProductPresentationProps;
    wrapper?: InjectableCss;
}

const referenceProductIdFormFieldStyles: ReferenceProductIdFormFieldStyles = {
    wrapper: {
        css: css`
            width: 100%;
            border: 1px solid ${getColor("common.borderLight")};
            border-radius: ${pxToRem(8)};
        `,
    },
    checkboxWrapper: {
        css: css`
            padding: ${pxToRem(12, 16)};
        `,
    },
    checkbox: {
        mergeCss: true,
        css: css`
            span[role="checkbox"][aria-checked="true"]:not([aria-disabled="true"]) + label {
                color: ${getColor("primary")};
            }
        `,
    },
    productWrapper: {
        css: css`
            padding: ${pxToRem(8, 16)};
            border-top: 1px solid ${getColor("common.borderLight")};
        `,
    },
};

const ReferenceProductIdFormField: FC<ReferenceProductIdFormFieldProps> = ({
    defaultChecked = false,
    defaultValue = "",
    extendedStyles,
    label,
    onChange,
    onUpdate,
    overridedStyles,
    ...checkboxProps
}) => {
    const styles = useExpandProps(referenceProductIdFormFieldStyles, extendedStyles, overridedStyles);
    const dispatch = useAppDispatch();
    const [isChecked, setIsChecked] = useState(defaultChecked);

    const referenceProductId = useMemo(() => {
        return getGuidFromQueryString(constants.queryStringParameters.referenceProductId) ?? defaultValue;
    }, []);

    const { isLoading, value: product } = useProductState(referenceProductId);

    useEffect(() => {
        onUpdate?.({ isValid: true, value: product && !product.isNotFound && isChecked ? product.id : "" });

        if (referenceProductId && !isLoading && !product) {
            dispatch(
                loadProduct({
                    handleNotFoundError: true,
                    id: referenceProductId,
                    onSuccess: product => {
                        if (isChecked) {
                            onUpdate?.({ isValid: true, value: product.id });
                        }
                    },
                } as LoadProductParameter),
            );
        }
    }, []);

    if (!product || product.isNotFound) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.checkboxWrapper}>
                <Checkbox
                    {...styles.checkbox}
                    {...checkboxProps}
                    checked={isChecked}
                    onChange={(event, checked) => {
                        onChange?.(event, checked);
                        setIsChecked(checked);
                        onUpdate?.({ isValid: true, value: checked ? referenceProductId : "" });
                    }}
                >
                    {label || translate("Include Reference Product")}
                </Checkbox>
            </StyledWrapper>
            <StyledWrapper {...styles.productWrapper}>
                <ReferenceProduct {...styles.referenceProduct} productId={referenceProductId} />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default ReferenceProductIdFormField;
